import React from 'react';
import PropTypes from 'prop-types';
import { Cushion, Rectangle, Typography } from '@pitchero/react-ui';
import { DateTime } from 'luxon';
import { find, flatMap, isEmpty } from 'lodash';
import Icon from '../icon';
import { Trans } from '../../lib/i18n';
import ClubLink from '../club-link';

const routeForParams = (params) => {
  const { teamId, category, month } = params;
  let route = 'news';
  if (teamId) {
    route += '.team';
  }
  if (category) {
    route += '.category';
  }
  if (month) {
    route += '.month';
  }

  return route;
};

const clearFilterButton = (label, params) => {
  const route = routeForParams(params);

  return (
    <Cushion bottom="small" component="div">
      <Rectangle radius="rounded" fill="mercury" style={{ display: 'inline-block' }}>
        <Cushion horizontal={8} vertical={6} component="div" style={{ display: 'inline-block' }}>
          <ClubLink route={route} params={params}>
            <a>
              <Typography preset="name" color="black">
                {label}
              </Typography>
            </a>
          </ClubLink>
        </Cushion>
        <Rectangle radius="0 4px 4px 0" fill="alto" style={{ display: 'inline-block' }}>
          <Cushion all={6} component="div">
            <ClubLink route={route} params={params}>
              <a>
                <Icon name="cross" preset="name" color="black" />
              </a>
            </ClubLink>
          </Cushion>
        </Rectangle>
      </Rectangle>
    </Cushion>
  );
};

const NoResults = ({ category, month, sections, teamId, t }) => {
  const noFilters = isEmpty({ category, month, teamId });
  if (noFilters) {
    return (
      <Cushion
        all="large"
        component="div"
        style={{ maxWidth: 280, margin: '0 auto', textAlign: 'center' }}
      >
        <Cushion bottom="medium">
          <Typography preset="subtitle--large" component="div">
            <Trans i18nKey="news:no_results">Sorry, there are no news items to show</Trans>
          </Typography>
        </Cushion>
        <Cushion bottom="medium">
          <Typography preset="body--small" color="dovegrey" component="div">
            <Trans i18nKey="news:no_results_description">
              Looks like there isn’t any recent news
            </Trans>
          </Typography>
        </Cushion>
      </Cushion>
    );
  }

  const clearTeamFilterButton = () => {
    if (!teamId) {
      return null;
    }

    const selectedTeam = find(
      flatMap(sections, (section) => section.teams),
      { id: teamId },
    );
    return selectedTeam ? clearFilterButton(selectedTeam.name, { category, month }) : null;
  };

  const clearCategoryFilterButton = () =>
    category ? clearFilterButton(t(`common:${category}_activity_type`), { teamId, month }) : null;
  const clearMonthFilterButton = () =>
    month
      ? clearFilterButton(DateTime.fromFormat(`${month}-01`, 'yyyy-MM-dd').toFormat('MMM yy'), {
          teamId,
          category,
        })
      : null;

  return (
    <Cushion
      all="large"
      component="div"
      style={{ maxWidth: 300, margin: '0 auto', textAlign: 'center' }}
    >
      {clearTeamFilterButton()}
      {clearCategoryFilterButton()}
      {clearMonthFilterButton()}
      <Cushion bottom="medium">
        <Typography preset="subtitle--large" component="div">
          <Trans i18nKey="news:no_filtered_results">
            Sorry, there are no news items that match your filters
          </Trans>
        </Typography>
      </Cushion>
      <Cushion bottom="medium">
        <Typography preset="body--small" color="dovegrey" component="div">
          <Trans i18nKey="common:no_filtered_results_advice">
            Try changing or clearing your filters
          </Trans>
        </Typography>
      </Cushion>
      <ClubLink route="news">
        <a>
          <Typography preset="button" color="primary">
            <Trans i18nKey="common:clear_all_filters_cta">Clear all filters</Trans>
          </Typography>
        </a>
      </ClubLink>
    </Cushion>
  );
};

NoResults.defaultProps = {
  category: null,
  month: null,
  teamId: null,
};

NoResults.propTypes = {
  category: PropTypes.string,
  month: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      teams: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  teamId: PropTypes.number,
  t: PropTypes.func.isRequired,
};

export default NoResults;
